import { useMutation } from "@tanstack/react-query";
import { CircleAlert, KeyRound, Mail } from "lucide-react";
import { useState } from "react";
import { api, loadSession, saveSession } from "./api.js";

function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className="notice notice--error" role="alert">
      <CircleAlert size={18} />
      <span>{error instanceof Error ? error.message : "The request could not be completed."}</span>
    </div>
  );
}

function AccountSecurityPage({ mode }: { mode: "admin" | "client" }) {
  const isAdmin = mode === "admin";
  const currentEmail = loadSession()?.user.email ?? "";
  const [emailForm, setEmailForm] = useState({
    email: currentEmail,
    currentPassword: "",
  });
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const change = useMutation({
    mutationFn: () =>
      api<{ changed: boolean; signInAgain: boolean }>(`/api/v1/${mode}/account/password`, {
        method: "POST",
        body: JSON.stringify({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        }),
      }),
    onSuccess: () => {
      saveSession(null);
      window.location.assign(`${isAdmin ? "/login-admin" : "/login"}?passwordChanged=1`);
    },
  });
  const emailChange = useMutation({
    mutationFn: () =>
      api<{ changed: boolean; signInAgain: boolean; email: string }>(
        "/api/v1/admin/account/email",
        {
          method: "POST",
          body: JSON.stringify({
            currentPassword: emailForm.currentPassword,
            email: emailForm.email,
          }),
        },
      ),
    onSuccess: () => {
      saveSession(null);
      window.location.assign("/login-admin?emailChanged=1");
    },
  });
  const matches = form.newPassword === form.confirmPassword;
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Account security</h2>
          <p>
            Change your {isAdmin ? "administrator" : "client"} password. Every existing{" "}
            {isAdmin ? "administrator" : "client"} session is invalidated.
          </p>
        </div>
      </section>
      {isAdmin ? (
        <form
          className="card settings-form password-form"
          onSubmit={(event) => {
            event.preventDefault();
            emailChange.mutate();
          }}
        >
          <span className="operation-icon">
            <Mail size={24} />
          </span>
          <h3>Administrator email</h3>
          <p>Change the email used to sign in. All administrator sessions will be signed out.</p>
          <label>
            <span>New administrator email</span>
            <input
              type="email"
              autoComplete="email"
              value={emailForm.email}
              onChange={(event) => setEmailForm({ ...emailForm, email: event.target.value })}
              required
            />
          </label>
          <label>
            <span>Current password for email change</span>
            <input
              type="password"
              autoComplete="current-password"
              value={emailForm.currentPassword}
              onChange={(event) =>
                setEmailForm({ ...emailForm, currentPassword: event.target.value })
              }
              required
            />
          </label>
          <ErrorNotice error={emailChange.error} />
          <button
            type="submit"
            className="button button--primary"
            disabled={
              emailChange.isPending ||
              !emailForm.currentPassword ||
              !emailForm.email ||
              emailForm.email.trim().toLowerCase() === currentEmail.toLowerCase()
            }
          >
            {emailChange.isPending ? "Changing email…" : "Change email & sign out"}
          </button>
        </form>
      ) : null}
      <form
        className="card settings-form password-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (matches) change.mutate();
        }}
      >
        <span className="operation-icon">
          <KeyRound size={24} />
        </span>
        <label>
          <span>Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={(event) => setForm({ ...form, currentPassword: event.target.value })}
            required
          />
        </label>
        <label>
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={form.newPassword}
            onChange={(event) => setForm({ ...form, newPassword: event.target.value })}
            required
          />
          <small>At least 12 characters with uppercase, lowercase, number, and symbol.</small>
        </label>
        <label>
          <span>Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })}
            required
          />
        </label>
        {!matches ? <div className="form-error">The new passwords do not match.</div> : null}
        <ErrorNotice error={change.error} />
        <button
          type="submit"
          className="button button--primary"
          disabled={
            change.isPending || !matches || !form.currentPassword || form.newPassword.length < 12
          }
        >
          {change.isPending ? "Changing password…" : "Change password & sign out"}
        </button>
      </form>
    </div>
  );
}

export function ClientAccountPage() {
  return <AccountSecurityPage mode="client" />;
}

export function AdminAccountPage() {
  return <AccountSecurityPage mode="admin" />;
}
